import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, Paper, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import JornadaService from "../../services/JornadaService";
import { avisoConAccionSx } from "./temaJornada";
import CifrasJornada, { colorDiferencia } from "./CifrasJornada";
import ChipFichaje from "./ChipFichaje";
import ConfigUtils from "../../utils/Config";
import LogService from "../../services/LogService";
import ILog from "../../types/Log";
import IAlert from "../../types/Alert";
import { IEstadoJornada, IMisFichajes, TipoFichaje } from "../../types/Jornada";
import { aHHmm, conHora, diaSemanaCorto, diferenciaFichada, duracionFichada, HORA_VALIDA, horaFichaje, lunesDe, resumenFichajes, sumarDias } from "../../utils/Jornada";
import * as constantes from "../../common/Constantes";

// Pestaña «Mi jornada»: el botón para fichar según el estado, el contador en vivo y los fichajes de hoy. Todo el cálculo lo
// hace la API; aquí solo se muestra. El contador usa la hora del servidor (corrigiendo el reloj del dispositivo).
interface MiJornadaProps {
    setAlert: (alert: IAlert) => void;
}

type Fallo = { message?: string; response?: { status?: number; data?: { message?: string } } };

const ACCION: Record<TipoFichaje, { boton: string; pregunta: string; hecho: string }> = {
    entrada: { boton: 'Fichar entrada', pregunta: 'la entrada', hecho: 'Entrada registrada' },
    salida: { boton: 'Fichar salida', pregunta: 'la salida', hecho: 'Salida registrada' },
    inicioPausa: { boton: 'Iniciar pausa', pregunta: 'el inicio de la pausa', hecho: 'Pausa iniciada' },
    finPausa: { boton: 'Reanudar', pregunta: 'la vuelta de la pausa', hecho: 'Jornada reanudada' },
};

const SEGUNDOS_DESHACER = 10;

const hora = horaFichaje;
const duracion = duracionFichada;
const horaCorta = (hhmm: string) => hhmm.replace(/^0(\d)/, '$1');

const MiJornada = ({ setAlert }: MiJornadaProps) => {
    const history = useNavigate();
    const [estado, setEstado] = useState<IEstadoJornada | null>(null);
    const [loading, setLoading] = useState(true);
    // Diferencia entre el reloj del servidor y el del dispositivo, para el contador y la hora del diálogo
    const desfase = useRef(0);
    const [, setTick] = useState(0);
    const [confirmar, setConfirmar] = useState<TipoFichaje | null>(null);
    const [enviando, setEnviando] = useState(false);
    const [errorEnvio, setErrorEnvio] = useState('');
    const [deshacer, setDeshacer] = useState<{ eventoId: string; texto: string } | null>(null);
    // Hora declarada en el diálogo de fichar (null: la del servidor)
    const [horaDeclarada, setHoraDeclarada] = useState<string | null>(null);
    // «Cerrar jornada de ayer»
    const [cerrarAyer, setCerrarAyer] = useState(false);
    const [horaAyer, setHoraAyer] = useState('');

    const ahoraServidor = () => Date.now() + desfase.current;

    // Resumen de la semana en curso (de lunes a domingo; solo se muestran los días con horario o con fichajes)
    const [semana, setSemana] = useState<IMisFichajes | null>(null);
    const esMovil = useMediaQuery(useTheme().breakpoints.down('sm'));
    const cargarSemana = (hoy: string) => {
        const desde = lunesDe(hoy);
        JornadaService.getMisFichajes(desde, sumarDias(desde, 6))
            .then((response: { data: IMisFichajes }) => setSemana(response.data))
            .catch(() => setSemana(null));
    };

    // Cada estado nuevo (al cargar, fichar o deshacer) actualiza también la semana
    const recibir = (nuevo: IEstadoJornada) => {
        desfase.current = new Date(nuevo.ahora).getTime() - Date.now();
        setEstado(nuevo);
        cargarSemana(nuevo.fecha);
        // La barra actualiza el punto del icono (si la ayuda está activada)
        const registro = ConfigUtils.getRegistroJornada();
        window.dispatchEvent(new CustomEvent('jornadaActualizada', { detail: !!registro?.ayudas?.badge && nuevo.recordatorio !== null }));
    };

    const tratarError = useCallback((e: unknown, metodo: string, porDefecto: string) => {
        const fallo = e as Fallo;
        if (fallo.response?.status === 401) {
            localStorage.clear();
            history(constantes.R_HOME);
            return;
        }
        if (![400, 403, 404].includes(fallo.response?.status ?? 0)) {
            const error: ILog = { method: `MiJornada.${metodo}`, level: 'error', message: fallo.message ?? '', meta: fallo.response };
            LogService.logError(error);
        }
        setAlert({ open: true, severity: 'error', msg: fallo.response?.data?.message || porDefecto });
    }, [history, setAlert]);

    const cargar = useCallback(() => {
        JornadaService.getEstado()
            .then((response: { data: IEstadoJornada }) => recibir(response.data))
            .catch((e: unknown) => tratarError(e, 'getEstado', 'No se ha podido cargar tu jornada'))
            .finally(() => setLoading(false));
    }, [tratarError]);

    useEffect(() => { cargar(); }, [cargar]);

    // El contador se repinta cada segundo
    useEffect(() => {
        const intervalo = setInterval(() => setTick(t => t + 1), 1000);
        return () => clearInterval(intervalo);
    }, []);

    // Envía un fichaje; al terminar bien cierra el diálogo que lo pidió. Sin conexión, el diálogo sigue abierto.
    const enviar = (tipo: TipoFichaje, horaIso: string | undefined, cerrarDialogo: () => void) => {
        setEnviando(true);
        setErrorEnvio('');
        JornadaService.fichar(tipo, horaIso)
            .then((response: { data: { evento: { _id: string; hora: string; origen: string }; estado: IEstadoJornada } }) => {
                recibir(response.data.estado);
                cerrarDialogo();
                const declarada = response.data.evento.origen === 'declarada' ? ' (hora declarada)' : '';
                setDeshacer({ eventoId: response.data.evento._id, texto: `${ACCION[tipo].hecho} ${hora(response.data.evento.hora)}${declarada}` });
            })
            .catch((e: unknown) => {
                const fallo = e as Fallo;
                if (!fallo.response) {
                    setErrorEnvio('No se ha registrado, vuelve a intentarlo');
                    return;
                }
                cerrarDialogo();
                tratarError(e, 'fichar', 'No se ha registrado, vuelve a intentarlo');
                cargar();
            })
            .finally(() => setEnviando(false));
    };

    const fichar = () => {
        if (!confirmar) return;
        let horaIso: string | undefined;
        if (horaDeclarada !== null) {
            if (!HORA_VALIDA.test(horaDeclarada)) { setErrorEnvio('Indica una hora válida'); return; }
            const elegida = conHora(new Date(ahoraServidor()), horaDeclarada);
            if (elegida.getTime() > ahoraServidor()) { setErrorEnvio('No puedes fichar una hora futura'); return; }
            horaIso = elegida.toISOString();
        }
        enviar(confirmar, horaIso, () => { setConfirmar(null); setHoraDeclarada(null); });
    };

    // Cierra la jornada de ayer con una salida declarada, posterior a su último fichaje y como muy tarde a las 23:59
    const ficharSalidaAyer = () => {
        if (!estado?.ayer) return;
        if (!HORA_VALIDA.test(horaAyer)) { setErrorEnvio('Indica la hora a la que saliste'); return; }
        const salida = conHora(estado.ayer.fecha, horaAyer);
        if (salida.getTime() <= new Date(estado.ayer.ultimo.hora).getTime()) {
            setErrorEnvio(`La salida tiene que ser posterior a las ${hora(estado.ayer.ultimo.hora)}`);
            return;
        }
        enviar('salida', salida.toISOString(), () => { setCerrarAyer(false); setHoraAyer(''); });
    };

    const deshacerUltimo = () => {
        if (!deshacer) return;
        const { eventoId } = deshacer;
        setDeshacer(null);
        JornadaService.deshacer(eventoId)
            .then((response: { data: { estado: IEstadoJornada } }) => {
                recibir(response.data.estado);
                setAlert({ open: true, severity: 'info', msg: 'Fichaje deshecho' });
            })
            .catch((e: unknown) => { tratarError(e, 'deshacer', 'No se ha podido deshacer, vuelve a intentarlo'); cargar(); });
    };

    if (loading) return <CircularProgress />;
    if (!estado) return null;
    if (!estado.puedeFichar) {
        return <Typography variant="body1">El registro de jornada ya no está activo para ti. Vuelve a iniciar sesión.</Typography>;
    }

    // Lo trabajado sigue sumando mientras está en jornada; la pausa cuenta desde su inicio
    const transcurrido = ahoraServidor() - new Date(estado.ahora).getTime();
    const trabajado = estado.trabajadoMs + (estado.estado === 'enJornada' ? transcurrido : 0);
    const enPausaMs = estado.estado === 'enPausa' && estado.estadoDesde ? ahoraServidor() - new Date(estado.estadoDesde).getTime() : 0;

    const { principal, secundaria, texto } = (() => {
        switch (estado.estado) {
            // Sin pausas en la clínica, solo la salida (en turno partido, salida y nueva entrada)
            case 'enJornada': return { principal: 'salida' as TipoFichaje, secundaria: estado.pausas ? 'inicioPausa' as TipoFichaje : null, texto: `En jornada · ${duracion(trabajado)}` };
            case 'enPausa': return { principal: 'finPausa' as TipoFichaje, secundaria: 'salida' as TipoFichaje, texto: `En pausa · ${duracion(enPausaMs)}` };
            case 'cerrada': return { principal: 'entrada' as TipoFichaje, secundaria: null, texto: `Jornada cerrada · Has trabajado ${duracion(trabajado)}` };
            default: return { principal: 'entrada' as TipoFichaje, secundaria: null, texto: 'Aún no has fichado hoy' };
        }
    })();

    const lineas = resumenFichajes(estado.eventos);
    const tramos = estado.tramosPrevistos.map(t => `${horaCorta(t.inicio)}–${horaCorta(t.fin)}`).join(' · ');

    return (
        <Box sx={{ maxWidth: 640 }}>
            {estado.ayer && (
                <Alert severity="warning" sx={{ mb: 2, ...avisoConAccionSx }}
                    action={<Button color="inherit" size="small" sx={{ whiteSpace: 'nowrap' }} onClick={() => { setErrorEnvio(''); setHoraAyer(''); setCerrarAyer(true); }}>Cerrar jornada de ayer</Button>}>
                    Ayer no fichaste la salida ({estado.ayer.enPausa ? 'estabas en pausa desde' : 'último fichaje'} a las {hora(estado.ayer.ultimo.hora)}).
                </Alert>
            )}

            {/* Estado, contador y botones, destacados en una tarjeta */}
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>{texto}</Typography>

            {/* Las cifras del día: con la jornada abierta, lo que queda; cerrada, la diferencia */}
            {estado.esFestivo
                ? <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Hoy es festivo: no hay horas previstas.</Typography>
                : estado.tieneHorario && tramos
                    ? <Box sx={{ mb: 2 }}>
                        <CifrasJornada cifras={[
                            { etiqueta: 'Fichado', valor: duracion(trabajado) },
                            { etiqueta: 'Previsto', valor: duracion(estado.previstoMs) },
                            estado.estado !== 'cerrada' && trabajado < estado.previstoMs
                                ? { etiqueta: 'Quedan', valor: duracion(estado.previstoMs - trabajado) }
                                : { etiqueta: 'Diferencia', valor: diferenciaFichada(trabajado - estado.previstoMs), color: colorDiferencia(trabajado - estado.previstoMs) },
                        ]} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Tu horario de hoy: {tramos}</Typography>
                    </Box>
                    : estado.tieneHorario
                        ? <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Hoy no tienes horas previstas en tu horario.</Typography>
                        : null}

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Button variant="contained" size="large" sx={{ px: 4, py: 1.5, fontSize: '1.05rem', width: { xs: '100%', sm: 'auto' } }} onClick={() => { setErrorEnvio(''); setHoraDeclarada(null); setConfirmar(principal); }}>
                    {principal === 'entrada' && estado.estado === 'cerrada' ? 'Fichar nueva entrada' : ACCION[principal].boton}
                </Button>
                {secundaria && (
                    <Button variant="outlined" size="large" sx={{ width: { xs: '100%', sm: 'auto' } }} onClick={() => { setErrorEnvio(''); setHoraDeclarada(null); setConfirmar(secundaria); }}>
                        {ACCION[secundaria].boton}
                    </Button>
                )}
            </Box>
            </Paper>

            {lineas.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Fichajes de hoy</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {lineas.map((l, i) => <ChipFichaje key={i} linea={l} />)}
                    </Box>
                </Box>
            )}

            {/* RESUMEN DE LA SEMANA: tabla en escritorio, lista en el móvil */}
            {semana && (() => {
                // Hoy cuenta hasta ahora (el contador sigue sumando)
                const filas = semana.dias
                    .filter(d => d.tieneHorario || d.eventos.length > 0)
                    .map(d => (d.fecha === estado.fecha ? { ...d, trabajadoMs: trabajado, diferenciaMs: trabajado - d.previstoMs } : d));
                if (filas.length === 0) return null;
                // El total, de los días terminados (hoy cuenta cuando se cierra la jornada), para que cuadre con la diferencia
                const terminados = filas.filter(d => d.fecha < estado.fecha || (d.fecha === estado.fecha && estado.estado === 'cerrada'));
                const total = (campo: 'trabajadoMs' | 'previstoMs') => terminados.reduce((s, d) => s + d[campo], 0);
                const hasta = filas.some(d => d.fecha === estado.fecha) && estado.estado !== 'cerrada' ? 'hasta ayer' : 'hasta hoy';
                // Un día incompleto no tiene nada fichado que contar
                const fichado = (d: typeof filas[number]) => (d.eventos.length && d.estado !== 'incompleta' ? duracion(d.trabajadoMs) : '—');
                // Hoy, mientras la jornada sigue, lo que queda; los días que faltan, nada
                const diferencia = (d: typeof filas[number]) => d.fecha > estado.fecha
                    ? ''
                    : d.fecha === estado.fecha && estado.estado !== 'cerrada'
                        ? (d.trabajadoMs < d.previstoMs ? `Quedan ${duracion(d.previstoMs - d.trabajadoMs)}` : diferenciaFichada(d.diferenciaMs))
                        : diferenciaFichada(d.diferenciaMs);
                const etiquetas = (d: typeof filas[number]) => (
                    <>
                        {d.esFestivo && <Chip size="small" label="Festivo" sx={{ ml: 1 }} />}
                        {d.estado === 'incompleta' && <Chip size="small" color="warning" label="Incompleta" sx={{ ml: 1 }} />}
                        {d.fecha < estado.fecha && d.eventos.length === 0 && d.previstoMs > 0 && <Chip size="small" variant="outlined" label="Sin fichajes" sx={{ ml: 1 }} />}
                    </>
                );
                // Color solo en las diferencias ya cerradas (no en «Quedan…» de hoy ni en los días que faltan)
                const colorFila = (d: typeof filas[number]) =>
                    d.fecha < estado.fecha || (d.fecha === estado.fecha && estado.estado === 'cerrada') ? colorDiferencia(d.diferenciaMs) : undefined;
                const diferenciaSemanaMs = total('trabajadoMs') - total('previstoMs');
                const diferenciaSemana = diferenciaFichada(diferenciaSemanaMs);
                return (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Esta semana</Typography>
                        {esMovil ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {filas.map(d => (
                                    <Paper key={d.fecha} variant="outlined" sx={{ px: 1.5, py: 1, bgcolor: d.fecha === estado.fecha ? 'action.selected' : undefined }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{diaSemanaCorto(d.fecha)}{etiquetas(d)}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {fichado(d)}
                                            {d.previstoMs ? ` de ${duracion(d.previstoMs)}` : ''}
                                            {diferencia(d) && <> · <Box component="span" sx={{ color: colorFila(d) }}>{diferencia(d)}</Box></>}
                                        </Typography>
                                    </Paper>
                                ))}
                                {terminados.length > 0 && (
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                                        Total {hasta}: {duracion(total('trabajadoMs'))} de {duracion(total('previstoMs'))} · <Box component="span" sx={{ color: colorDiferencia(diferenciaSemanaMs) }}>{diferenciaSemana}</Box>
                                    </Typography>
                                )}
                            </Box>
                        ) : (
                            <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Día</TableCell>
                                        <TableCell align="right">Fichado</TableCell>
                                        <TableCell align="right">Previsto</TableCell>
                                        <TableCell align="right">Diferencia</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filas.map(d => (
                                        <TableRow key={d.fecha} selected={d.fecha === estado.fecha}>
                                            <TableCell>{diaSemanaCorto(d.fecha)}{etiquetas(d)}</TableCell>
                                            <TableCell align="right">{fichado(d)}</TableCell>
                                            <TableCell align="right">{d.previstoMs ? duracion(d.previstoMs) : '—'}</TableCell>
                                            <TableCell align="right" sx={{ color: colorFila(d) }}>{diferencia(d)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {terminados.length > 0 && (
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>Total {hasta}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{duracion(total('trabajadoMs'))}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{duracion(total('previstoMs'))}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold', color: colorDiferencia(diferenciaSemanaMs) }}>{diferenciaSemana}</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        )}
                    </Box>
                );
            })()}

            {/* CONFIRMAR EL FICHAJE */}
            <Dialog open={confirmar !== null} maxWidth="xs" fullWidth onClose={() => !enviando && setConfirmar(null)}>
                <DialogTitle>{confirmar ? ACCION[confirmar].boton : ''}</DialogTitle>
                <DialogContent>
                    {horaDeclarada === null ? (
                        <>
                            <DialogContentText>¿Fichar {confirmar ? ACCION[confirmar].pregunta : ''} a las {hora(ahoraServidor())}?</DialogContentText>
                            <Button variant="text" size="small" sx={{ textTransform: 'none', p: 0, mt: 1, minWidth: 0 }} onClick={() => setHoraDeclarada(aHHmm(new Date(ahoraServidor())))}>
                                Cambiar la hora
                            </Button>
                        </>
                    ) : (
                        <>
                            <DialogContentText sx={{ mb: 2 }}>¿A qué hora de hoy fue {confirmar ? ACCION[confirmar].pregunta : ''}?</DialogContentText>
                            <TextField type="time" size="small" label="Hora" value={horaDeclarada} inputProps={{ step: 60 }} onChange={(e) => setHoraDeclarada(e.target.value)} />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Se guardará como hora declarada.</Typography>
                        </>
                    )}
                    {errorEnvio && <Alert severity="error" sx={{ mt: 2 }}>{errorEnvio}</Alert>}
                </DialogContent>
                <DialogActions>
                    {enviando ? <CircularProgress size={24} sx={{ mr: 2 }} /> : (
                        <>
                            <Button onClick={fichar} variant="contained">FICHAR</Button>
                            <Button onClick={() => setConfirmar(null)}>CANCELAR</Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* CERRAR JORNADA DE AYER */}
            <Dialog open={cerrarAyer} maxWidth="xs" fullWidth onClose={() => !enviando && setCerrarAyer(false)}>
                <DialogTitle>Cerrar jornada de ayer</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        ¿A qué hora saliste ayer? Tu último fichaje fue a las {estado.ayer ? hora(estado.ayer.ultimo.hora) : ''}.
                    </DialogContentText>
                    <TextField type="time" size="small" label="Hora de salida" value={horaAyer} InputLabelProps={{ shrink: true }} inputProps={{ step: 60 }} onChange={(e) => setHoraAyer(e.target.value)} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Se guardará como hora declarada.</Typography>
                    {errorEnvio && <Alert severity="error" sx={{ mt: 2 }}>{errorEnvio}</Alert>}
                </DialogContent>
                <DialogActions>
                    {enviando ? <CircularProgress size={24} sx={{ mr: 2 }} /> : (
                        <>
                            <Button onClick={ficharSalidaAyer} variant="contained">FICHAR SALIDA</Button>
                            <Button onClick={() => setCerrarAyer(false)}>CANCELAR</Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* DESHACER (10 s; la API acepta hasta 15) */}
            <Snackbar open={deshacer !== null} autoHideDuration={SEGUNDOS_DESHACER * 1000} onClose={(_e, reason) => reason !== 'clickaway' && setDeshacer(null)}>
                <Alert severity="success" variant="filled" sx={{ width: '100%' }}
                    action={<Button color="inherit" size="small" onClick={deshacerUltimo}>DESHACER</Button>}>
                    {deshacer?.texto}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default MiJornada;
